/**
 * @module Header
 * @description Represents the header component of the application.
 * @returns {JSX.Element} The header component.
 */
import React, { useContext, lazy, Suspense } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navbar, Nav, NavDropdown, Form } from 'react-bootstrap'
import { useNavigate, Link } from 'react-router-dom'
import { ThemeContext } from './ThemeContext.js'

const SearchBox = lazy(() => import('../components/SearchBox.js'))

// Header component
const Header = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme, isDarkMode, setIsDarkMode } = useContext(ThemeContext)

  // Get login state
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin
  //
  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin
  const isPublicHeader = !userInfo && !ownerInfo

  const navbarStyle = {
    '--header-text': theme.text,
    '--header-subtext': theme.subtext,
    '--header-card': theme.card,
    '--header-border': isDarkMode ? '#2D342D' : '#DFE6E9',
    '--header-hover-bg': isDarkMode ? '#2F3A33' : '#EDF3EE',
    '--header-active-bg': isDarkMode ? '#3A4A40' : '#E1EAE3',
    '--header-top-hover-bg': isDarkMode
      ? 'rgba(223, 255, 0, 0.12)'
      : 'rgba(110, 127, 107, 0.14)',
    '--header-top-active-bg': isDarkMode
      ? 'rgba(223, 255, 0, 0.2)'
      : 'rgba(110, 127, 107, 0.22)',
    '--header-focus-ring': isDarkMode
      ? 'rgba(223, 255, 0, 0.3)'
      : 'rgba(110, 127, 107, 0.35)',
    backgroundColor: theme.card,
    borderColor: isDarkMode ? '#040404' : '#DFE6E9'
  }

  // Logout handler
  const logoutHandler = async () => {
    if (userInfo) {
      const id = userInfo._id
      const { logout } = await import('../actions/userActions.js')
      dispatch(logout(id))
    } else if (ownerInfo) {
      const id = ownerInfo._id
      const { logoutOwner } = await import('../actions/ownerActions.js')
      dispatch(logoutOwner(id))
      navigate('/')
    } else {
      navigate('/')
    }
    // Clear cookies
    document.cookie = 'jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
    // Clear session and localstorage
    if (userInfo) {
      sessionStorage.removeItem('userInfo')
      localStorage.removeItem('userInfo')
    } else {
      sessionStorage.removeItem('ownerInfo')
      localStorage.removeItem('ownerInfo')
    }
  }

  if (isPublicHeader) {
    return (
      <Navbar
        className='themed-header px-4 py-3 border-bottom shadow-sm'
        variant={isDarkMode ? 'dark' : 'light'}
        style={navbarStyle}
        expand='lg'
      >
        <Navbar.Brand
          as={Link}
          to='/'
          className='fw-bold'
          style={{ color: theme.text }}
        >
          <span style={{ color: theme.accent }}>🌿</span> Global Receipt
        </Navbar.Brand>

        <Navbar.Toggle aria-controls='basic-navbar-nav' />
        <Navbar.Collapse id='basic-navbar-nav'>
          <Nav className='ms-auto d-flex align-items-center gap-3'>
            <Form.Check
              type='switch'
              id='theme-switch'
              label={isDarkMode ? '🌙 Dark' : '☀️ Light'}
              checked={isDarkMode}
              onChange={() => setIsDarkMode(!isDarkMode)}
              className='small'
              style={{ color: theme.subtext }}
            />
            <NavDropdown
              title={<span style={{ color: theme.text }}>Menu</span>}
              id='basic-nav-dropdown'
              className='themed-nav-dropdown'
              menuVariant={isDarkMode ? 'dark' : undefined}
            >
              <NavDropdown.Item as={Link} to='/login'>
                User Login
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/register'>
                User Register
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/ownerlogin'>
                Business Login
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/businessregister'>
                Business Register
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/about'>
                About Us
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    )
  }

  return (
    <Navbar
      className='themed-header px-4 py-3 border-bottom shadow-sm'
      variant={isDarkMode ? 'dark' : 'light'}
      style={navbarStyle}
      expand='lg'
    >
      <Navbar.Brand className='right-align'>
        <span style={{ color: theme.accent }}>🌿</span> Global Receipt
      </Navbar.Brand>

      <Navbar.Toggle aria-controls='basic-navbar-nav' />
      <Navbar.Collapse id='basic-navbar-nav'>
        {userInfo && (
          <Suspense fallback={null}>
            <SearchBox />
          </Suspense>
        )}

        <Nav className='ms-auto d-flex align-items-center gap-3'>
          <Form.Check
            type='switch'
            id='theme-switch'
            label={isDarkMode ? '🌙 Dark' : '☀️ Light'}
            checked={isDarkMode}
            onChange={() => setIsDarkMode(!isDarkMode)}
            className='small'
            style={{ color: theme.subtext }}
          />

          {userInfo && (
            <Navbar.Brand as={Link} to='/receipts' className='small ml-right'>
              Receipts
            </Navbar.Brand>
          )}

          {userInfo && !userInfo.isAdmin && (
            <NavDropdown
              title={userInfo.name}
              id='username'
              className='themed-nav-dropdown'
              menuVariant={isDarkMode ? 'dark' : undefined}
            >
              <NavDropdown.Item as={Link} to='/dashboard'>
                Dashboard
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/reports'>
                Reports
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/profile'>
                Profile
              </NavDropdown.Item>
              <NavDropdown.Item href='mailto:info@globalreceipt.ca'>
                Contact Us
              </NavDropdown.Item>
              <NavDropdown.Item onClick={logoutHandler}>
                Logout
              </NavDropdown.Item>
            </NavDropdown>
          )}

          {userInfo && userInfo.isAdmin && (
            <NavDropdown
              title={userInfo.name}
              id='username'
              className='themed-nav-dropdown'
              menuVariant={isDarkMode ? 'dark' : undefined}
            >
              <NavDropdown.Item as={Link} to='/userlist'>
                Users
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/dashboard'>
                Dashboard
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/reports'>
                Reports
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/profile'>
                Profile
              </NavDropdown.Item>
              <NavDropdown.Item href='mailto:info@globalreceipt.ca'>
                Contact Us
              </NavDropdown.Item>
              <NavDropdown.Item onClick={logoutHandler}>
                Logout
              </NavDropdown.Item>
            </NavDropdown>
          )}

          {ownerInfo && (
            <NavDropdown title={ownerInfo.name} id='ownername'>
              <NavDropdown.Item as={Link} to='/ownerdashboard'>
                Dashboard
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/owneranalytics'>
                Analytics
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/receiptchart'>
                Receipt Count Chart
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/ownerprofile'>
                Profile
              </NavDropdown.Item>
              <NavDropdown.Item href='mailto:info@globalreceipt.ca'>
                Contact Us
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to='/apidocument'>
                API Document
              </NavDropdown.Item>
              <NavDropdown.Item onClick={logoutHandler}>
                Logout
              </NavDropdown.Item>
            </NavDropdown>
          )}
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  )
}

export default Header
